import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DxDiagramComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { ViewHierarchyDetailsComponent } from '../modals/organization/view-hierarchy-details/view-hierarchy-details.component';
import { TaggedItemType } from '../../models/instance/instance.model';
import { environment } from 'src/environments/environment';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { EnvironmentService } from 'src/app/services/environment.service';

interface Point {
    x: number;
    y: number;
}

interface Connector {
    key: string;
    locked: boolean;
    zIndex: number;
    points: Point[];
    beginItemKey: string;
    beginConnectionPointIndex: number;
    endItemKey: string;
    endConnectionPointIndex: number;
}

interface Shape {
    key: string;
    locked: boolean;
    zIndex: number;
    type: string;
    text: string;
    x: number;
    y: number;
    width: number;
    height: number;
    taggedItemType?: any;
}

interface TreeNode {
    key: string;
    type: string;
    text: string;
    taggedItemType?: any;
    children: TreeNode[];
}

@Component({
    selector: 'app-flowchart',
    templateUrl: './flowchart.component.html',
    styleUrls: ['./flowchart.component.scss'],
})
export class FlowchartComponent implements OnInit {
    @Input()
    set customHiearchy(value: any) {
        this._customHiearchy = value;
    }
    get customHiearchy(): any {
        return this._customHiearchy;
    }

    @Input()
    set diagramJson(value: any) {
        if (value) {
            this._diagramJson = value;
            this._diagramJson.shapes.forEach((element) => {
                element.imageUrl = '';
            });
            this._rawDiagramJson = JSON.stringify(this._diagramJson);
        }
    }
    get diagramJson(): any {
        return this._diagramJson;
    }

    @Input()
    set taggedItemsTypes(value: TaggedItemType[]) {
        if (value) {
            const customItem: TaggedItemType = {
                name: 'Custom',
                icon: this.EnvironmentService.getConfig().blobPublic + 'Management/Icon/TaggedItemType/Custom.png',
                canAllocate: false,
                description: 'Custom',
                schemaRevision: { schema: '[]' },
                isCustom: true,
            };
            if (!value.find((m) => m.isCustom == true)) value.push(customItem);
            this._taggedItemsTypes = value;
            this.taggedItemDataMap.clear();

            this._taggedItemsTypes.forEach((taggedItemType) => {
                // const obj = {
                //     name: taggedItemType.name,
                //     icon: taggedItemType.icon,
                //     canAllocate: taggedItemType?.canAllocate || false,
                //     description: taggedItemType?.description || null,
                //     schemaRevision: { schema: taggedItemType.schemaVersions[0].schema },
                // };
                // this.taggedItemDataMap.set(taggedItemType.name, obj);
                // taggedItemType.schemaVersions[0].version = 1;
                this.taggedItemDataMap.set(taggedItemType.name, { ...taggedItemType });
            });

            if (this.diagramReady) {
                this.intializeDiagram();
            }
        }
    }

    get taggedItemsTypes(): TaggedItemType[] {
        return this._taggedItemsTypes;
    }

    @ViewChild(DxDiagramComponent, { static: false }) diagram: DxDiagramComponent;
    @ViewChild('diagramContainer', { static: false }) diagramContainer: ElementRef;
    @Output() genratedHierarchy: EventEmitter<any> = new EventEmitter();

    diagramReady: boolean = false;
    taggedItemDataMap = new Map<string, TaggedItemType>();
    taggedItems: TaggedItemType[];
    customItem: TaggedItemType = {
        name: 'Custom',
        icon: this.EnvironmentService.getConfig().blobPublic + 'Management/Icon/TaggedItemType/Custom.png',
        canAllocate: false,
        description: 'Custom',
        schemaRevision: { schema: '[]' },
    };
    private _customHiearchy;
    private _diagramJson: any;
    private _rawDiagramJson: string = '';
    private _taggedItemsTypes: TaggedItemType[];

    constructor(
        private globalEmittersService: GlobalEmittersService,
        private modelService: NgbModal,
        private toastrService: CustomToastService,
        private EnvironmentService: EnvironmentService
    ) {}

    ngOnInit() {
        this.globalEmittersService.useHiearchyClick.subscribe(async (res) => {
            if (res) {
                await this.onExport();
            }
        });
    }

    ngAfterViewInit(): void {
        this.diagramReady = true;
        // If inputs already came in
        if (this._taggedItemsTypes?.length) {
            this.intializeDiagram();
        }
    }

    intializeDiagram() {
        setTimeout(() => {
            if (this.diagram?.instance) {
                const diagram = this.diagram.instance;
                const jsonToImport = this._rawDiagramJson || JSON.stringify({ shapes: [], connectors: [] });

                diagram.import(jsonToImport);
                diagram.option('pageSize', { width: 10, height: 6 });
            } else {
                console.warn('Diagram instance is not available yet.');
            }
        }, 0);
    }

    async onExport() {
        if (!this.diagram || !this.diagram.instance) {
            this.showToast('Diagram is not initialized.');
            return;
        }

        const diagramInstance = this.diagram.instance;
        const allItems = diagramInstance.getItems?.(); // Add optional chaining here
        if (!allItems) {
            // this.showToast('Unable to retrieve diagram items.');
            return;
        }

        const shapes = allItems?.filter((item) => item.itemType === 'shape') as Array<any>;
        let orphans = this.findOrphanedConnectors(allItems);
        let orphanedShapes = shapes.filter((x) => !x?.attachedConnectorIds?.length);
        const names = shapes.map((p) => p.text);
        const hasDuplicate = new Set(names).size !== names.length;
        if (hasDuplicate) {
            this.toastrService.warningToast('Duplicate data found!!');
            return;
        } else if (orphanedShapes?.length || orphans?.length) {
            this.showToast('Unable to use design. Please check for orphaned tagged items.');
            orphanedShapes = null;
            orphans = null;
            return;
        } else {
            const diagramData = await diagramInstance.export();
            const diagramJson = JSON.parse(diagramData);
            diagramJson.shapes.forEach((shape: any) => {
                let data = this._taggedItemsTypes.find((m) => m.name == shape.type);
                shape.imageUrl = data.icon;
                if (this._diagramJson) {
                    let taggedItemtype = this._diagramJson.shapes.find((s) => s.key === shape.key)?.taggedItemType;
                    if (taggedItemtype) {
                        shape.taggedItemType = taggedItemtype;
                    }
                }
            });
            this._diagramJson = diagramJson;
            this._rawDiagramJson = diagramData;

            const data = await this.buildTree(diagramJson.shapes, diagramJson.connectors);

            if (data?.length) {
                if (data.length > 1) {
                    this.showToast('Unable to use design. Please check for orphaned tagged items.');
                } else {
                    this._diagramJson = null;
                    this._rawDiagramJson = '';
                    this._customHiearchy = null;
                    this._taggedItemsTypes = [];
                    this.taggedItemDataMap.clear();
                    this.genratedHierarchy.next({ data, diagramJson });
                }
            }
        }

        orphanedShapes = null;
    }

    findOrphanedConnectors(items: any[]) {
        const shapes = items.filter((item) => item.itemType === 'shape');
        const shapeIds = new Set(shapes.map((shape) => shape.id));

        const connectors = items.filter((item) => item.itemType === 'connector');

        const orphanedConnectors = connectors.filter((conn) => {
            const fromOk = conn.fromId && shapeIds.has(conn.fromId);
            const toOk = conn.toId && shapeIds.has(conn.toId);
            return !fromOk || !toOk;
        });

        return orphanedConnectors;
    }

    traverseTree(nodeOrList: TreeNode | TreeNode[], callback: (node: TreeNode & { taggedItemType?: any }) => void): void {
        const traverse = (node: TreeNode) => {
            // Get tagged item info from map using node.type
            const taggedItemType = this.taggedItemDataMap.get(node.type);
            // Attach it to the node
            (node as any) = taggedItemType;
            // Now pass to your callback
            callback(node as TreeNode & { taggedItemType?: any });
            // Recurse on children
            (node.children || []).forEach((child) => traverse(child));
        };

        if (Array.isArray(nodeOrList)) {
            nodeOrList.forEach((node) => traverse(node));
        } else {
            traverse(nodeOrList);
        }
    }

    async buildTree(shapes: Shape[], connectors: Connector[]) {
        const shapeMap = new Map<string, Shape>();
        const connectorMap = new Map<string, Connector[]>();

        shapes.forEach((shape) => shapeMap.set(shape.key, shape));
        connectors.forEach((connector) => {
            if (!connectorMap.has(connector.beginItemKey)) {
                connectorMap.set(connector.beginItemKey, []);
            }
            connectorMap.get(connector.beginItemKey)?.push(connector);
        });

        const buildNode = (key: string) => {
            const shape = shapeMap.get(key);
            const children: any[] = [];

            if (!shape) {
                return {
                    key,
                    type: '',
                    text: '',
                    taggedItemType: null,
                };
            }
            if (connectorMap.has(key)) {
                connectorMap.get(key)?.forEach((connector) => {
                    children.push(buildNode(connector.endItemKey));
                });
            }
            let taggedItemType;
            if (shape.type != 'Custom') {
                taggedItemType = this.taggedItemDataMap.get(shape.text);
                taggedItemType.isCustom = false;
            } else {
                const customItem: TaggedItemType = {
                    name: 'Custom',
                    icon: this.EnvironmentService.getConfig().blobPublic + 'Management/Icon/TaggedItemType/Custom.png',
                    canAllocate: false,
                    description: 'Custom',
                    schemaRevision: { schema: '[]' },
                    isCustom: true,
                };
                if (this._diagramJson) {
                    let type = this._diagramJson.shapes.find((s) => s.key === shape.key)?.taggedItemType;
                    if (type) {
                        taggedItemType = type;
                        taggedItemType.isCustom = true;
                    } else {
                        taggedItemType = customItem;
                    }
                } else {
                    taggedItemType = customItem;
                }
            }
            // Build base node
            const node: any = {
                taggedItemType: { ...taggedItemType },
            };
            // Only add children if non-empty
            if (children.length > 0) {
                node.children = children;
            }
            return node;
        };

        const rootKeys = shapes.map((shape) => shape.key).filter((key) => !connectors.some((connector) => connector.endItemKey === key));
        return rootKeys.map(buildNode);
    }

    requestEditOperationHandler(e) {
        if (e.operation === 'changeConnection') {
            const fromId = e.args?.connector?.fromId;
            const toId = e.args?.connector?.toId;

            // Optional: detect and prevent cycles
            if (this.createsCycle(fromId, toId)) {
                e.allowed = false;
                this.showToast('Unable to use the design. Circular reference identified.');
                return;
            }

            // Optional: prevent certain types from connecting
            const targetType = e.args.newShape?.type;
            if (targetType === 'root' && e.args.connectorPosition === 'end') {
                e.allowed = false;
                this.showToast('Unable to use the design. Circular reference identified.');
                return;
            }

            const shapeType = e.args.newShape && e.args.newShape.type;
            if (shapeType === 'Joint' && e.args.connectorPosition === 'start') {
                e.allowed = false;
            }
        }

        const { fromShape } = e.args;
        // Prevent any connection from or to "Joint" type shapes
        if (fromShape?.text.toLowerCase() === 'joint') {
            e.allowed = false;
        }
        if (e.operation === 'resizeShape') {
            e.allowed = false;
            return;
        }
    }

    // Detects if adding a connection from 'fromId' to 'toId' creates a cycle
    createsCycle(fromId: string, toId: string): boolean {
        if (!!fromId) {
            const diagram = this.diagram.instance;
            const visited = new Set<string>();

            // Build adjacency list from current diagram connectors
            const graph = new Map<string, string[]>();
            const connectors = diagram.getItems().filter((item) => item.itemType === 'connector');

            for (const conn of connectors) {
                const src = conn['fromId']; // Type-casting since fromId is not typed on dxDiagramItem
                const dest = conn['toId'];
                if (!graph.has(src)) {
                    graph.set(src, []);
                }
                graph.get(src)!.push(dest);
            }

            // Temporarily add the new edge
            if (!graph.has(fromId)) {
                graph.set(fromId, []);
            }
            graph.get(fromId)!.push(toId);

            // Depth-First Search (DFS) to detect cycle
            const hasCycle = (node: string, ancestors: Set<string>): boolean => {
                if (ancestors.has(node)) return true; // cycle found
                if (visited.has(node)) return false;

                visited.add(node);
                ancestors.add(node);

                const neighbors = graph.get(node) || [];
                for (const neighbor of neighbors) {
                    if (hasCycle(neighbor, ancestors)) {
                        return true;
                    }
                }

                ancestors.delete(node);
                return false;
            };

            return hasCycle(fromId, new Set());
        }
    }

    showToast(text: string) {
        notify(
            {
                message: text,
                position: {
                    at: 'top center',
                    my: 'top center',
                    of: '#diagram',
                    offset: '10 10',
                },
                width: 600,
                minWidth: 600,
                displayTime: 3000,
                shading: false,
                closeOnClick: true,
                closeOnSwipe: true,
                height: 45,
            },
            'Error'
        );
    }

    async editTaggedItem(item) {
        const dialogRef = this.modelService.open(ViewHierarchyDetailsComponent, {
            keyboard: false,
            backdrop: 'static',
            size: 'lg',
        });

        if (item.type !== 'Custom') {
            const tagged = this.taggedItemDataMap.get(item.type);
            dialogRef.componentInstance.taggedItem = tagged;
        } else {
            // const tagged = this.taggedItemDataMap.get(item.type);
            // dialogRef.componentInstance.taggedItem = tagged;
            // dialogRef.componentInstance.isCustom = true;
            if (this._diagramJson) {
                let targetShape = this._diagramJson.shapes.find((s) => s.key === item.id);
                // }
                if (targetShape?.taggedItemType) {
                    let tagged = targetShape.taggedItemType;
                    dialogRef.componentInstance.taggedItem = tagged;
                    dialogRef.componentInstance.isCustom = true;
                } else {
                    const customItem: TaggedItemType = {
                        name: 'Custom',
                        icon: this.EnvironmentService.getConfig().blobPublic + 'Management/Icon/TaggedItemType/Custom.png',
                        canAllocate: false,
                        description: 'Custom',
                        schemaRevision: { schema: '[]' },
                    };
                    //let tagged: TaggedItemType = this._taggedItemsTypes.find((s) => s.name === item.type);
                    dialogRef.componentInstance.taggedItem = customItem;
                    // dialogRef.componentInstance.taggedItem = tagged;
                    dialogRef.componentInstance.isCustom = true;
                    // const tagged = this.taggedItemDataMap.get(item.type);
                    // dialogRef.componentInstance.taggedItem = tagged;
                    // dialogRef.componentInstance.isCustom = true;
                }
            } else {
                const customItem: TaggedItemType = {
                    name: 'Custom',
                    icon: this.EnvironmentService.getConfig().blobPublic + 'Management/Icon/TaggedItemType/Custom.png',
                    canAllocate: false,
                    description: 'Custom',
                    schemaRevision: { schema: '[]' },
                };
                //let tagged: TaggedItemType = this._taggedItemsTypes.find((s) => s.name === item.type);
                dialogRef.componentInstance.taggedItem = customItem;
                dialogRef.componentInstance.isCustom = true;
                // const tagged = this.taggedItemDataMap.get(item.type);
                // dialogRef.componentInstance.taggedItem = tagged;
                // dialogRef.componentInstance.isCustom = true;
            }
        }

        dialogRef.result.then(async (result) => {
            if (result) {
                const diagram = this.diagram?.instance;
                if (!diagram) {
                    this.showToast('Diagram instance not available.');
                    return;
                }

                const diagramData = await diagram.export();
                const diagramJson = JSON.parse(diagramData);

                diagramJson.shapes.forEach((element) => {
                    if (element.key === item.id) {
                        element.text = result.name;
                        // targetShape.type = result.name;
                        element.taggedItemType = result;
                    } else if (this._diagramJson) {
                        let taggedItemtype = this._diagramJson.shapes.find((s) => s.key === element.key)?.taggedItemType;
                        if (taggedItemtype) {
                            element.taggedItemType = taggedItemtype;
                        }
                    }
                });
                // const _diagram = this.diagram?.instance;
                // const _diagramData = await _diagram.export();
                // const _diagramJson = JSON.parse(_diagramData);
                // Save updates
                this._diagramJson = diagramJson;
                this._rawDiagramJson = JSON.stringify(diagramJson);

                // Re-import updated diagram
                diagram.import(this._rawDiagramJson);

                // Update the data map
                this.taggedItemDataMap.set(result.name, result);
                // const targetShape = diagramJson.shapes.find((s) => s.key === item.id);
                // if (targetShape) {
                //     targetShape.text = result.name;
                //     // targetShape.type = result.name;
                //     targetShape.taggedItemType = result;

                //     // Save updates
                //     this._diagramJson = diagramJson;
                //     this._rawDiagramJson = JSON.stringify(diagramJson);

                //     // Re-import updated diagram
                //     diagram.import(this._rawDiagramJson);

                //     // Update the data map
                //     this.taggedItemDataMap.set(result.name, result);
                //     // this.taggedItemDataMap.set(result.name, { ...result });
                // } else {
                //     this.showToast('Shape not found in diagram data.');
                // }
            }
        });
    }

    async deleteTaggedItem(item) {
        if (!item?.id) return;
        const diagram = this.diagram?.instance;
        const diagramData = await diagram.export();
        const diagramJson = JSON.parse(diagramData);

        // const targetShape = diagramJson.shapes.find((s) => s.key === item.id);

        // this._diagramJson = diagramJson.shapes.filter((s) => s.key !== item.id);
        const index = diagramJson.shapes.findIndex((shape) => shape.key === item.id);
        if (index !== -1) {
            diagramJson.shapes.splice(index, 1);
        }
        diagramJson.shapes.forEach((element) => {
            let taggedItemType;
            if (element.type != 'Custom') {
                taggedItemType = this.taggedItemDataMap.get(element.text);
            } else {
                if (this._diagramJson) {
                    let type = this._diagramJson.shapes.find((s) => s.key === element.key)?.taggedItemType;
                    if (type) {
                        element.taggedItemType = type;
                    } else {
                        const customItem: TaggedItemType = {
                            name: 'Custom',
                            icon: this.EnvironmentService.getConfig().blobPublic + 'Management/Icon/TaggedItemType/Custom.png',
                            canAllocate: false,
                            description: 'Custom',
                            schemaRevision: { schema: '[]' },
                        };
                        element.taggedItemType = customItem;
                    }
                }
            }
        });
        // this._diagramJson.connectors = this._diagramJson.connectors.filter((c) => c.beginItemKey !== item.id && c.endItemKey !== item.id);

        const updatedJson = JSON.stringify(diagramJson);
        this._rawDiagramJson = updatedJson;

        if (this.diagram?.instance) {
            this.diagram.instance.import(updatedJson);
        } else {
            console.warn('Diagram instance not available for re-import.');
        }
    }
    getIcon(tmp: any) {
        let data = this._taggedItemsTypes.find((m) => m.name == tmp.name);
        return data.icon;
    }
    ngOnDestroy(): void {
        this._diagramJson = null;
        this._rawDiagramJson = '';
        this._customHiearchy = null;
        this._taggedItemsTypes = [];
        this.taggedItemDataMap.clear();
    }
}
